/**
 * Theme management system
 *
 * Supports:
 *   - Firm-based theme configuration (loaded from API, stored in theme_config)
 *   - Dark mode toggle with localStorage persistence
 *   - System preference detection for dark mode
 *   - Real-time theme preview
 */

import type { ThemeConfig, ThemeColors } from '@/types/settings';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export type ColorMode = 'light' | 'dark' | 'system';

/** LocalStorage key for color mode preference */
const COLOR_MODE_KEY = 'sentra-color-mode';

/** LocalStorage key for cached firm theme */
const FIRM_THEME_CACHE_KEY = 'sentra-firm-theme-v2'; // v2: brand_color colorways

// ---------------------------------------------------------------------------
// Brand palette
// ---------------------------------------------------------------------------
//
// One firm colour drives the UI. The sidebar and primary buttons use the brand
// colour itself (only its lightness is clamped so white text stays readable);
// surfaces, borders and text are quiet neutrals carrying a trace of the hue.

/** Sentra indigo: the default when a firm has no brand_color. */
export const SENTRA_INDIGO = '#170098';

/** Suggested brand colours for the picker. */
export const COLORWAYS = [
  { name: 'Indigo', hex: '#170098' },
  { name: 'Cobalt', hex: '#00277a' },
  { name: 'Violet', hex: '#4d0066' },
  { name: 'Evergreen', hex: '#003d1b' },
  { name: 'Teal', hex: '#00494d' },
  { name: 'Crimson', hex: '#630010' },
] as const;

const INK = '#1b1a18';

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function hsl(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%)`;
}

/** HSL of a #rrggbb colour; falls back to Sentra indigo. */
export function hslOf(hex: string | null | undefined): { h: number; s: number; l: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return hslOf(SENTRA_INDIGO);
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  const l = (mx + mn) / 2;
  if (d === 0) return { h: 0, s: 0, l: l * 100 };
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = mx === r ? ((g - b) / d + 6) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: h * 60, s: s * 100, l: l * 100 };
}

/** The headline colours derived from a brand colour (also used for previews). */
export function colorway(brandHex: string | null | undefined) {
  const { h, s, l } = hslOf(brandHex);
  const sat = clamp(s, 25, 90);
  return {
    brand: hsl(h, sat, clamp(l, 16, 38)),               // sidebar + primary buttons
    brandHover: hsl(h, sat, clamp(l, 16, 38) + 6),
    accent: hsl(h, clamp(s, 35, 75), 42),               // links, focus, rail hover
    canvas: hsl(h, Math.min(sat, 18), 97),              // page background
    brandDark: hsl(h, clamp(s, 35, 80), 70),            // primary on dark sheets
    h,
    sat,
  };
}

/** Complete light + dark ThemeConfig derived from one brand colour. */
export function themeFromBrandColor(brandHex: string | null | undefined): ThemeConfig {
  const cw = colorway(brandHex);
  const { h } = cw;
  const n = Math.min(cw.sat, 12); // neutral saturation: a trace of the hue

  // The sidebar is the brand colour in both light and dark mode.
  const rail = {
    sidebar_background: cw.brand,
    sidebar_foreground: 'rgba(255, 255, 255, 0.82)',
    sidebar_primary: '#ffffff',
    sidebar_primary_foreground: cw.brand,
    // Auth pages also use this on light cards (links, focus rings), so it must
    // read on both grounds.
    sidebar_accent: cw.accent,
    sidebar_accent_foreground: '#ffffff',
    sidebar_border: 'rgba(255, 255, 255, 0.12)',
    sidebar_ring: 'rgba(255, 255, 255, 0.5)',
  };

  // Analogous brand hues + one contrast (steel blue, or amber for blue brands)
  const hueDist = Math.min(Math.abs(h - 205), 360 - Math.abs(h - 205));
  const contrast = hueDist < 60 ? 32 : 205;
  const charts = (l: number) => [
    hsl(h, clamp(cw.sat, 45, 70), l),
    hsl((h + 40) % 360, 45, l + 10),
    hsl((h + 320) % 360, 40, l + 4),
    hsl(contrast, 55, l + 2),
    hsl(h, 8, l + 8),
  ];
  const [c1, c2, c3, c4, c5] = charts(45);
  const [d1, d2, d3, d4, d5] = charts(62);

  const colors: ThemeColors = {
    background: cw.canvas,
    foreground: INK,
    card: '#ffffff',
    card_foreground: INK,
    popover: '#ffffff',
    popover_foreground: INK,
    primary: cw.brand,
    primary_foreground: '#ffffff',
    secondary: hsl(h, n, 94),
    secondary_foreground: INK,
    muted: hsl(h, n, 95),
    muted_foreground: hsl(h, 6, 42),
    accent: hsl(h, n + 6, 94), // hover / selected surface in menus and lists
    accent_foreground: INK,
    destructive: '#c62828',
    destructive_foreground: '#ffffff',
    border: hsl(h, n, 90),
    input: hsl(h, n, 85),
    // Neutral focus ring: a brand-red ring would read as a validation error
    ring: hsl(h, 8, 45),
    chart_1: c1, chart_2: c2, chart_3: c3, chart_4: c4, chart_5: c5,
    ...rail,
  };

  // Dark mode: neutral charcoal surfaces (a 3% trace of the hue at most; more
  // turns them muddy), a deep brand tone for the sidebar instead of the loud
  // light-mode colour, and a softened brand for primary buttons.
  const d = Math.min(cw.sat, 3);
  const darkRail = {
    ...rail,
    sidebar_background: hsl(h, Math.min(cw.sat, 45), 12),
    sidebar_primary_foreground: hsl(h, Math.min(cw.sat, 45), 12),
    sidebar_accent: hsl(h, clamp(cw.sat, 30, 55), 55),
    sidebar_border: 'rgba(255, 255, 255, 0.08)',
  };

  const dark_colors: ThemeColors = {
    background: hsl(h, d, 7),
    foreground: '#ececee',
    card: hsl(h, d, 10),
    card_foreground: '#ececee',
    popover: hsl(h, d, 12),
    popover_foreground: '#ececee',
    primary: hsl(h, clamp(cw.sat, 35, 60), 48),
    primary_foreground: '#ffffff',
    secondary: hsl(h, d, 16),
    secondary_foreground: '#ececee',
    muted: hsl(h, d, 14),
    muted_foreground: hsl(h, d, 64),
    accent: hsl(h, d, 16),
    accent_foreground: '#ececee',
    destructive: '#e5484d',
    destructive_foreground: '#ffffff',
    border: hsl(h, d, 18),
    input: hsl(h, d, 24),
    ring: hsl(h, d, 60),
    chart_1: d1, chart_2: d2, chart_3: d3, chart_4: d4, chart_5: d5,
    ...darkRail,
  };

  return { colors, dark_colors, radius: '0.625rem' };
}

/** Derive and apply the palette for a firm's brand colour. */
export function applyBrandColor(brandHex: string | null | undefined): void {
  applyFirmTheme(themeFromBrandColor(brandHex));
}

// ---------------------------------------------------------------------------
// CSS variable mapping
// ---------------------------------------------------------------------------

/** Maps ThemeColors keys to CSS variable names */
const COLOR_KEY_TO_CSS_VAR: Record<keyof ThemeColors, string> = {
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  card_foreground: '--card-foreground',
  popover: '--popover',
  popover_foreground: '--popover-foreground',
  primary: '--primary',
  primary_foreground: '--primary-foreground',
  secondary: '--secondary',
  secondary_foreground: '--secondary-foreground',
  muted: '--muted',
  muted_foreground: '--muted-foreground',
  accent: '--accent',
  accent_foreground: '--accent-foreground',
  destructive: '--destructive',
  destructive_foreground: '--destructive-foreground',
  border: '--border',
  input: '--input',
  ring: '--ring',
  chart_1: '--chart-1',
  chart_2: '--chart-2',
  chart_3: '--chart-3',
  chart_4: '--chart-4',
  chart_5: '--chart-5',
  sidebar_background: '--sidebar-background',
  sidebar_foreground: '--sidebar-foreground',
  sidebar_primary: '--sidebar-primary',
  sidebar_primary_foreground: '--sidebar-primary-foreground',
  sidebar_accent: '--sidebar-accent',
  sidebar_accent_foreground: '--sidebar-accent-foreground',
  sidebar_border: '--sidebar-border',
  sidebar_ring: '--sidebar-ring',
};

// ---------------------------------------------------------------------------
// Dark Mode
// ---------------------------------------------------------------------------

/** Get the stored color mode preference */
export function getColorMode(): ColorMode {
  const stored = localStorage.getItem(COLOR_MODE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'light'; // Default to light
}

/** Set color mode and apply it */
export function setColorMode(mode: ColorMode): void {
  localStorage.setItem(COLOR_MODE_KEY, mode);
  applyColorMode(mode);
}

/** Check whether dark mode is currently active */
export function isDarkModeActive(): boolean {
  return document.documentElement.classList.contains('dark');
}

/** Apply the color mode to the document */
function applyColorMode(mode: ColorMode): void {
  const html = document.documentElement;
  if (mode === 'dark') {
    html.classList.add('dark');
  } else if (mode === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    html.classList.toggle('dark', prefersDark);
  } else {
    html.classList.remove('dark');
  }
}

// ---------------------------------------------------------------------------
// Firm-based theming
// ---------------------------------------------------------------------------

/** Get the cached firm theme from localStorage */
export function getCachedFirmTheme(): ThemeConfig | null {
  try {
    const cached = localStorage.getItem(FIRM_THEME_CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch {
    // ignore parse errors
  }
  return null;
}

/** Cache the firm theme to localStorage for fast initial load */
export function cacheFirmTheme(config: ThemeConfig): void {
  try {
    localStorage.setItem(FIRM_THEME_CACHE_KEY, JSON.stringify(config));
  } catch {
    // ignore storage errors
  }
}

/** Clear the cached firm theme */
export function clearFirmThemeCache(): void {
  localStorage.removeItem(FIRM_THEME_CACHE_KEY);
}

/**
 * Apply a firm's theme_config to the document.
 * Sets CSS custom properties on the :root element for both light and dark modes.
 */
export function applyFirmTheme(config: ThemeConfig | null | undefined): void {
  if (!config) return;

  // Remove any previously injected firm theme style
  let styleEl = document.getElementById('sentra-firm-theme');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'sentra-firm-theme';
    document.head.appendChild(styleEl);
  }

  // Build CSS for :root (light) and .dark overrides
  let css = '';

  // Light mode colors
  if (config.colors && Object.keys(config.colors).length > 0) {
    const lightVars = buildCSSVars(config.colors);
    if (lightVars) {
      css += `:root { ${lightVars} }\n`;
    }
  }

  // Dark mode colors
  if (config.dark_colors && Object.keys(config.dark_colors).length > 0) {
    const darkVars = buildCSSVars(config.dark_colors);
    if (darkVars) {
      css += `.dark { ${darkVars} }\n`;
    }
  }

  // Radius
  if (config.radius) {
    css += `:root { --radius: ${config.radius}; }\n`;
  }

  styleEl.textContent = css;

  // Cache for fast reload
  cacheFirmTheme(config);
}

/** Build a string of CSS variable declarations from a partial ThemeColors object */
function buildCSSVars(colors: Partial<ThemeColors>): string {
  const entries: string[] = [];
  for (const [key, value] of Object.entries(colors)) {
    const cssVar = COLOR_KEY_TO_CSS_VAR[key as keyof ThemeColors];
    if (cssVar && value) {
      entries.push(`${cssVar}: ${value};`);
    }
  }
  return entries.join(' ');
}

/**
 * Initialize theme on page load.
 * Applies cached firm theme (if any) and restores color mode preference.
 */
export function initializeTheme(): void {
  // Apply cached firm theme for fast initial render
  const cached = getCachedFirmTheme();
  if (cached) {
    applyFirmTheme(cached);
  }

  // Restore color mode
  applyColorMode(getColorMode());
}
